/**
 * @jsxRuntime classic
 * @jsx jsx
 */
/* eslint-disable @compiled/shorthand-property-sorting -- Skeleton layout mirrors the page grid and uses logical-axis overrides. */
import React from 'react';

import { useIntl } from 'react-intl';

import { getDocument } from '@atlaskit/browser-apis';
import { cssMap, cx, jsx } from '@atlaskit/css';
import StatusErrorIcon from '@atlaskit/icon/core/status-error';
import { Box } from '@atlaskit/primitives/compiled/box';
import { Flex } from '@atlaskit/primitives/compiled/flex';
import { Stack } from '@atlaskit/primitives/compiled/stack';
import { Text } from '@atlaskit/primitives/compiled/text';
import { token } from '@atlaskit/tokens';

import { messages } from '../messages';

const SKELETON_STYLE_ID = 'dtac-inferred-projects-skeleton-pulse';
const TWG_SPINNER_VIEWBOX_SIZE = 24;
const TWG_SPINNER_CENTER = TWG_SPINNER_VIEWBOX_SIZE / 2;
const TWG_SPINNER_DOT_RADIUS = 2.5;
const TWG_SPINNER_DOT_STROKE_WIDTH = 1.5;
const TWG_SPINNER_SPREAD_RADIUS = 8;
const TWG_SPINNER_LINE_WIDTH = 1.5;
const TWG_SPINNER_BURST_DURATION_S = 0.4;
const TWG_SPINNER_ROTATE_DURATION_S = 0.4;
const TWG_SPINNER_DRAW_DURATION_S = 0.8;
const TWG_SPINNER_TAIL_DELAY = 0.35;
const TWG_SPINNER_ROTATION_AMOUNT_RAD = (45 * Math.PI) / 180;
const TWG_SPINNER_HIDDEN_DASH = '0 9999';
const TWG_SPINNER_DOT_ANGLES = [-Math.PI / 2, 0, Math.PI / 2, Math.PI] as const;
const TWG_SPINNER_DOT_ORDER = [0, 1, 3, 2] as const;
const TWG_SPINNER_DRAW_PATTERNS = [
	[
		[3, 0],
		[0, 2],
		[2, 1],
	],
	[
		[0, 2],
		[2, 3],
		[3, 1],
	],
	[
		[3, 1],
		[1, 0],
		[0, 2],
	],
	[
		[0, 1],
		[1, 3],
		[3, 2],
	],
] as const;
const TWG_SPINNER_TOTAL_SNAKE_DURATION_S =
	TWG_SPINNER_DRAW_DURATION_S / (1 - TWG_SPINNER_TAIL_DELAY);
const TWG_SPINNER_CYCLE_DURATION_S =
	TWG_SPINNER_TOTAL_SNAKE_DURATION_S + TWG_SPINNER_ROTATE_DURATION_S;
const TWG_SPINNER_LINE_COUNT = 3;

type SpinnerPosition = {
	x: number;
	y: number;
};

type SpinnerFrame = {
	positions: SpinnerPosition[];
	drawFrac: number;
	retractFrac: number;
	patternIndex: number;
};

const clamp01 = (value: number): number => Math.min(1, Math.max(0, value));

const easeOutBack = (value: number, overshoot = 1): number => {
	const clamped = clamp01(value);
	const c3 = overshoot + 1;
	return 1 + c3 * Math.pow(clamped - 1, 3) + overshoot * Math.pow(clamped - 1, 2);
};

const easeInOutQuad = (value: number): number => {
	const clamped = clamp01(value);
	return clamped < 0.5 ? 2 * clamped * clamped : 1 - Math.pow(-2 * clamped + 2, 2) / 2;
};

const getSpinnerPositions = (spread: number, rotation: number): SpinnerPosition[] =>
	TWG_SPINNER_DOT_ANGLES.map((angle) => ({
		x: TWG_SPINNER_CENTER + Math.cos(angle + rotation) * spread,
		y: TWG_SPINNER_CENTER + Math.sin(angle + rotation) * spread,
	}));

const getSpinnerFrame = (elapsedSeconds: number): SpinnerFrame => {
	let spread = TWG_SPINNER_SPREAD_RADIUS;
	let rotation = 0;
	let drawFrac = -1;
	let retractFrac = 0;
	let patternIndex = 0;

	if (elapsedSeconds < TWG_SPINNER_BURST_DURATION_S) {
		spread =
			easeOutBack(elapsedSeconds / TWG_SPINNER_BURST_DURATION_S, 0.8) * TWG_SPINNER_SPREAD_RADIUS;
	} else {
		const cycleElapsed = elapsedSeconds - TWG_SPINNER_BURST_DURATION_S;
		const cycleCount = Math.floor(cycleElapsed / TWG_SPINNER_CYCLE_DURATION_S);
		const cycleTime = cycleElapsed % TWG_SPINNER_CYCLE_DURATION_S;
		const baseRotation = cycleCount * TWG_SPINNER_ROTATION_AMOUNT_RAD;

		patternIndex = cycleCount % TWG_SPINNER_DRAW_PATTERNS.length;

		if (cycleTime < TWG_SPINNER_TOTAL_SNAKE_DURATION_S) {
			const snakeProgress = cycleTime / TWG_SPINNER_TOTAL_SNAKE_DURATION_S;
			drawFrac = easeInOutQuad(Math.min(1, snakeProgress / (1 - TWG_SPINNER_TAIL_DELAY)));
			retractFrac = easeInOutQuad(
				Math.min(
					1,
					Math.max(0, (snakeProgress - TWG_SPINNER_TAIL_DELAY) / (1 - TWG_SPINNER_TAIL_DELAY)),
				),
			);
			rotation = baseRotation;
		} else {
			const rotateProgress =
				(cycleTime - TWG_SPINNER_TOTAL_SNAKE_DURATION_S) / TWG_SPINNER_ROTATE_DURATION_S;
			rotation = baseRotation + easeOutBack(rotateProgress, 0.6) * TWG_SPINNER_ROTATION_AMOUNT_RAD;
		}
	}

	return {
		positions: getSpinnerPositions(spread, rotation),
		drawFrac,
		retractFrac,
		patternIndex,
	};
};

const getStaticSpinnerFrame = (): SpinnerFrame => ({
	positions: getSpinnerPositions(TWG_SPINNER_SPREAD_RADIUS, 0),
	drawFrac: -1,
	retractFrac: 0,
	patternIndex: 0,
});

const hideSpinnerLine = (line: SVGPathElement): void => {
	line.setAttribute('stroke-dasharray', TWG_SPINNER_HIDDEN_DASH);
	line.setAttribute('stroke-dashoffset', '0');
};

const updateSpinnerDots = (svg: SVGSVGElement, positions: readonly SpinnerPosition[]): void => {
	const circles = svg.querySelectorAll<SVGCircleElement>('[data-type="dot"], [data-type="mask"]');

	circles.forEach((circle) => {
		const index = Number(circle.getAttribute('data-idx'));
		const position = positions[index];

		if (!position) {
			return;
		}

		circle.setAttribute('cx', String(position.x));
		circle.setAttribute('cy', String(position.y));
	});
};

const updateSpinnerLines = (
	svg: SVGSVGElement,
	positions: readonly SpinnerPosition[],
	pattern: readonly (readonly [number, number])[],
	drawFrac: number,
	retractFrac: number,
): void => {
	const segmentLengths: number[] = [];
	const cumulativeDistance: number[] = [0];

	for (let index = 0; index < pattern.length; index += 1) {
		const [fromIndex, toIndex] = pattern[index];
		const from = positions[fromIndex];
		const to = positions[toIndex];
		const length = Math.sqrt(Math.pow(to.x - from.x, 2) + Math.pow(to.y - from.y, 2));

		segmentLengths.push(length);
		cumulativeDistance.push(cumulativeDistance[index] + length);
	}

	const totalLength = cumulativeDistance[pattern.length];
	const headDistance = drawFrac * totalLength;
	const tailDistance = retractFrac * totalLength;

	for (let index = 0; index < TWG_SPINNER_LINE_COUNT; index += 1) {
		const line = svg.getElementById(`twg-spinner-line-${index}`) as SVGPathElement | null;

		if (!line) {
			continue;
		}

		if (index >= pattern.length || drawFrac < 0) {
			hideSpinnerLine(line);
			continue;
		}

		const [fromIndex, toIndex] = pattern[index];
		const from = positions[fromIndex];
		const to = positions[toIndex];
		const segmentLength = segmentLengths[index];
		const segmentStart = cumulativeDistance[index];
		const headPosition = Math.min(segmentLength, Math.max(0, headDistance - segmentStart));
		const tailPosition = Math.min(segmentLength, Math.max(0, tailDistance - segmentStart));
		const visibleLength = headPosition - tailPosition;

		if (visibleLength <= 0.001 || segmentLength < 0.001) {
			hideSpinnerLine(line);
			continue;
		}

		line.setAttribute('d', `M${from.x},${from.y} L${to.x},${to.y}`);
		line.setAttribute(
			'stroke-dasharray',
			`${visibleLength.toFixed(4)} ${(segmentLength - visibleLength).toFixed(4)}`,
		);
		line.setAttribute('stroke-dashoffset', (-tailPosition).toFixed(4));
	}
};

const ensureSkeletonStyles = () => {
	const doc = getDocument();

	if (!doc || doc.getElementById(SKELETON_STYLE_ID)) {
		return;
	}

	const style = doc.createElement('style');
	style.id = SKELETON_STYLE_ID;
	style.textContent = `
@keyframes dtacInferredProjectsSkeletonPulse {
	0%, 100% { opacity: 1; }
	50% { opacity: 0.56; }
}
`;
	doc.head.appendChild(style);
};

const useSkeletonStyles = () => {
	React.useEffect(() => {
		ensureSkeletonStyles();
	});
};

const styles = cssMap({
	pageRoot: {
		height: '100%',
		overflowY: 'auto',
		overflowX: 'hidden',
		backgroundColor: token('elevation.surface'),
	},
	layout: {
		display: 'grid',
		gridTemplateColumns: '320px minmax(0, 1fr)',
		minHeight: '100%',
	},
	sidebarColumn: {
		minWidth: '0px',
		borderInlineEndWidth: token('border.width'),
		borderInlineEndStyle: 'solid',
		borderInlineEndColor: token('color.border'),
		backgroundColor: token('elevation.surface'),
	},
	sidebarPanel: {
		position: 'sticky',
		top: token('space.0'),
		paddingBlock: token('space.300'),
		paddingInline: token('space.200'),
	},
	contentShell: {
		minWidth: '0px',
		paddingBlock: token('space.300'),
		paddingInline: token('space.600'),
	},
	contentLayout: {
		display: 'grid',
		alignItems: 'flex-start',
		gridTemplateColumns: 'minmax(0, 1fr) 312px',
		columnGap: token('space.200'),
		maxWidth: '1300px',
	},
	mainColumn: {
		minWidth: '0px',
	},
	rightRail: {
		minWidth: '0px',
		paddingBlockStart: token('space.800'),
	},
	card: {
		borderWidth: token('border.width'),
		borderStyle: 'solid',
		borderColor: token('color.border'),
		borderRadius: token('radius.small'),
		backgroundColor: token('elevation.surface'),
		paddingBlock: token('space.150'),
		paddingInline: token('space.150'),
	},
	paddedCard: {
		borderWidth: token('border.width'),
		borderStyle: 'solid',
		borderColor: token('color.border'),
		borderRadius: token('radius.small'),
		backgroundColor: token('elevation.surface'),
		paddingBlock: token('space.200'),
		paddingInline: token('space.200'),
	},
	metricsRoot: {
		display: 'grid',
		gridTemplateColumns: 'repeat(4, minmax(0, 1fr))',
		borderWidth: token('border.width'),
		borderStyle: 'solid',
		borderColor: token('color.border'),
		borderRadius: token('radius.small'),
		backgroundColor: token('elevation.surface'),
		overflow: 'hidden',
	},
	metricCell: {
		minWidth: '0px',
		paddingBlock: token('space.150'),
		paddingInline: token('space.150'),
		borderInlineStartWidth: token('border.width'),
		borderInlineStartStyle: 'solid',
		borderInlineStartColor: token('color.border'),
	},
	metricFirstCell: {
		minWidth: '0px',
		paddingBlock: token('space.150'),
		paddingInline: token('space.150'),
		borderInlineStartWidth: 0,
	},
	recentGrid: {
		display: 'grid',
		gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
		gap: token('space.150'),
	},
	timelineEntry: {
		display: 'grid',
		gridTemplateColumns: '104px minmax(0, 1fr) 32px',
		columnGap: token('space.200'),
		paddingBlock: token('space.200'),
		paddingInline: token('space.200'),
	},
	bone: {
		backgroundColor: token('color.skeleton'),
		animationName: 'dtacInferredProjectsSkeletonPulse',
		animationDuration: '1.8s',
		animationTimingFunction: 'ease-in-out',
		animationIterationCount: 'infinite',

		'@media (prefers-reduced-motion: reduce)': {
			animationName: 'none',
		},
	},
	livingSummaryThinking: {
		minHeight: '170px',
		display: 'flex',
		flexDirection: 'column',
		alignItems: 'center',
		justifyContent: 'center',
		gap: token('space.150'),
		textAlign: 'center',
	},
	graphClawDisabledState: {
		minHeight: '170px',
		display: 'flex',
		flexDirection: 'column',
		alignItems: 'center',
		justifyContent: 'center',
		gap: token('space.150'),
		borderRadius: token('radius.small'),
		backgroundColor: token('elevation.surface'),
		paddingBlock: token('space.300'),
		paddingInline: token('space.300'),
		textAlign: 'center',
	},
	graphClawDisabledStateFramed: {
		borderWidth: token('border.width'),
		borderStyle: 'solid',
		borderColor: token('color.border'),
	},
	graphClawDisabledStateCompact: {
		minHeight: '112px',
		paddingBlock: token('space.200'),
		paddingInline: token('space.150'),
	},
	graphClawDisabledStateRoomy: {
		minHeight: '244px',
	},
	componentErrorState: {
		display: 'flex',
		alignItems: 'center',
		gap: token('space.100'),
		color: token('color.text.danger'),
	},
	twgSpinnerWrapper: {
		display: 'inline-flex',
	},
	twgSpinnerText: {
		maxWidth: '520px',
	},
	sidebarTitle: {
		width: '164px',
		height: '20px',
		borderRadius: token('radius.small'),
	},
	sidebarText: {
		width: '220px',
		height: '16px',
		borderRadius: token('radius.small'),
	},
	iconMedium: {
		width: '40px',
		height: '40px',
		borderRadius: token('radius.small'),
	},
	iconLarge: {
		width: '48px',
		height: '48px',
		borderRadius: token('radius.small'),
	},
	title: {
		width: '560px',
		maxWidth: '80%',
		height: '36px',
		borderRadius: token('radius.small'),
	},
	heading: {
		width: '160px',
		height: '18px',
		borderRadius: token('radius.small'),
	},
	metricLabel: {
		width: '88px',
		height: '16px',
		borderRadius: token('radius.small'),
	},
	metricValue: {
		width: '32px',
		height: '28px',
		borderRadius: token('radius.small'),
	},
	lineFull: {
		width: '100%',
		height: '16px',
		borderRadius: token('radius.small'),
	},
	lineLong: {
		width: '84%',
		height: '16px',
		borderRadius: token('radius.small'),
	},
	lineMedium: {
		width: '62%',
		height: '16px',
		borderRadius: token('radius.small'),
	},
	lineShort: {
		width: '42%',
		height: '16px',
		borderRadius: token('radius.small'),
	},
	date: {
		width: '44px',
		height: '14px',
		borderRadius: token('radius.small'),
	},
	pill: {
		width: '104px',
		height: '20px',
		borderRadius: token('radius.small'),
	},
	avatar: {
		width: '32px',
		height: '32px',
		borderRadius: token('radius.full'),
	},
	avatarSmall: {
		width: '24px',
		height: '24px',
		borderRadius: token('radius.full'),
	},
	goalRow: {
		width: '100%',
		height: '34px',
		borderRadius: token('radius.small'),
	},
});

type SkeletonBoneVariant =
	| 'avatar'
	| 'avatarSmall'
	| 'date'
	| 'goalRow'
	| 'heading'
	| 'iconLarge'
	| 'iconMedium'
	| 'lineFull'
	| 'lineLong'
	| 'lineMedium'
	| 'lineShort'
	| 'metricLabel'
	| 'metricValue'
	| 'pill'
	| 'sidebarText'
	| 'sidebarTitle'
	| 'title';

const SkeletonBone = ({ variant }: { variant: SkeletonBoneVariant }): React.JSX.Element => (
	<div aria-hidden="true" css={[styles.bone, styles[variant]]} />
);

const usePrefersReducedMotion = (): boolean => {
	const [prefersReducedMotion, setPrefersReducedMotion] = React.useState(false);

	React.useEffect(() => {
		const view = getDocument()?.defaultView;

		if (!view) {
			return;
		}

		const mediaQueryList = view.matchMedia('(prefers-reduced-motion: reduce)');
		const handleChange = () => setPrefersReducedMotion(mediaQueryList.matches);

		handleChange();
		mediaQueryList.addEventListener('change', handleChange);

		return () => mediaQueryList.removeEventListener('change', handleChange);
	}, []);

	return prefersReducedMotion;
};

const TeamworkGraphSpinner = ({ size = 64 }: { size?: number }): React.JSX.Element => {
	const reducedMotion = usePrefersReducedMotion();
	const svgRef = React.useRef<SVGSVGElement>(null);
	const dotColors = [
		token('color.icon.accent.orange', '#FCA700'),
		token('color.icon.accent.lime', '#6A9A23'),
		token('color.icon.accent.blue', '#1868DB'),
		token('color.icon.accent.purple', '#AF59E0'),
	];
	// eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage
	const lineColor = '#6C6F77';
	const maskColor = token('elevation.surface', '#FFFFFF');

	React.useEffect(() => {
		const svg = svgRef.current;
		const view = svg?.ownerDocument.defaultView;

		if (!svg || !view) {
			return;
		}

		const applyFrame = ({ positions, drawFrac, retractFrac, patternIndex }: SpinnerFrame) => {
			updateSpinnerDots(svg, positions);
			updateSpinnerLines(
				svg,
				positions,
				TWG_SPINNER_DRAW_PATTERNS[patternIndex],
				drawFrac,
				retractFrac,
			);
		};

		if (reducedMotion) {
			applyFrame(getStaticSpinnerFrame());
			return;
		}

		let animationFrameId = 0;
		let startTime = 0;

		const tick = (timestamp: number) => {
			if (!startTime) {
				startTime = timestamp;
			}

			applyFrame(getSpinnerFrame((timestamp - startTime) / 1000));
			animationFrameId = view.requestAnimationFrame(tick);
		};

		animationFrameId = view.requestAnimationFrame(tick);

		return () => view.cancelAnimationFrame(animationFrameId);
	}, [reducedMotion]);

	return (
		<div aria-hidden="true" data-vc-ignore-if-no-layout-shift="true" css={styles.twgSpinnerWrapper}>
			<svg
				ref={svgRef}
				width={size}
				height={size}
				viewBox={`0 0 ${TWG_SPINNER_VIEWBOX_SIZE} ${TWG_SPINNER_VIEWBOX_SIZE}`}
				fill="none"
				aria-hidden="true"
				focusable="false"
			>
				<g>
					{Array.from({ length: TWG_SPINNER_LINE_COUNT }, (_, index) => (
						<path
							key={`line-${index}`}
							id={`twg-spinner-line-${index}`}
							stroke={lineColor}
							strokeWidth={TWG_SPINNER_LINE_WIDTH}
							strokeLinecap="butt"
							fill="none"
							strokeDasharray={TWG_SPINNER_HIDDEN_DASH}
						/>
					))}
				</g>
				<g>
					{TWG_SPINNER_DOT_ORDER.map((index) => (
						<circle
							key={`mask-${index}`}
							data-type="mask"
							data-idx={index}
							cx={TWG_SPINNER_CENTER}
							cy={TWG_SPINNER_CENTER}
							r={TWG_SPINNER_DOT_RADIUS}
							fill={maskColor}
							stroke="none"
						/>
					))}
				</g>
				<g>
					{TWG_SPINNER_DOT_ORDER.map((index) => (
						<circle
							key={`dot-${index}`}
							data-type="dot"
							data-idx={index}
							cx={TWG_SPINNER_CENTER}
							cy={TWG_SPINNER_CENTER}
							r={TWG_SPINNER_DOT_RADIUS}
							fill="none"
							stroke={dotColors[index]}
							strokeWidth={TWG_SPINNER_DOT_STROKE_WIDTH}
						/>
					))}
				</g>
			</svg>
		</div>
	);
};

const TeamworkGraphStaticIcon = ({ size = 48 }: { size?: number }): React.JSX.Element => {
	const positions = getSpinnerPositions(TWG_SPINNER_SPREAD_RADIUS, 0);
	const dotColors = [
		token('color.icon.accent.orange', '#FCA700'),
		token('color.icon.accent.lime', '#6A9A23'),
		token('color.icon.accent.blue', '#1868DB'),
		token('color.icon.accent.purple', '#AF59E0'),
	];
	// eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage
	const lineColor = '#6C6F77';
	const maskColor = token('elevation.surface', '#FFFFFF');

	return (
		<div aria-hidden="true" data-vc-ignore-if-no-layout-shift="true" css={styles.twgSpinnerWrapper}>
			<svg
				width={size}
				height={size}
				viewBox={`0 0 ${TWG_SPINNER_VIEWBOX_SIZE} ${TWG_SPINNER_VIEWBOX_SIZE}`}
				fill="none"
				aria-hidden="true"
				focusable="false"
			>
				<g>
					{TWG_SPINNER_DRAW_PATTERNS[0].map(([fromIndex, toIndex], index) => {
						const from = positions[fromIndex];
						const to = positions[toIndex];

						return (
							<path
								key={`line-${index}`}
								d={`M${from.x},${from.y} L${to.x},${to.y}`}
								stroke={lineColor}
								strokeWidth={TWG_SPINNER_LINE_WIDTH}
								strokeLinecap="butt"
								fill="none"
							/>
						);
					})}
				</g>
				<g>
					{TWG_SPINNER_DOT_ORDER.map((index) => (
						<circle
							key={`mask-${index}`}
							cx={positions[index].x}
							cy={positions[index].y}
							r={TWG_SPINNER_DOT_RADIUS}
							fill={maskColor}
							stroke="none"
						/>
					))}
				</g>
				<g>
					{TWG_SPINNER_DOT_ORDER.map((index) => (
						<circle
							key={`dot-${index}`}
							cx={positions[index].x}
							cy={positions[index].y}
							r={TWG_SPINNER_DOT_RADIUS}
							fill="none"
							stroke={dotColors[index]}
							strokeWidth={TWG_SPINNER_DOT_STROKE_WIDTH}
						/>
					))}
				</g>
			</svg>
		</div>
	);
};

type GraphClawDisabledStateProps = {
	isFramed?: boolean;
	variant?: 'default' | 'compact' | 'roomy';
};

export const GraphClawDisabledState = ({
	isFramed = true,
	variant = 'default',
}: GraphClawDisabledStateProps): React.JSX.Element => {
	const intl = useIntl();

	return (
		<Box
			xcss={cx(
				styles.graphClawDisabledState,
				isFramed && styles.graphClawDisabledStateFramed,
				variant === 'compact' && styles.graphClawDisabledStateCompact,
				variant === 'roomy' && styles.graphClawDisabledStateRoomy,
			)}
			role="note"
			aria-label={intl.formatMessage(messages.activateGraphClaw)}
		>
			<TeamworkGraphStaticIcon />
			<Box xcss={styles.twgSpinnerText}>
				<Text color="color.text.subtle" weight="semibold">
					{intl.formatMessage(messages.activateGraphClaw)}
				</Text>
			</Box>
		</Box>
	);
};

export const GoalsSkeleton = ({ count = 5 }: { count?: number }): React.JSX.Element => {
	useSkeletonStyles();

	return (
		<Stack space="space.100" role="status" aria-label="Loading goal recommendations">
			{Array.from({ length: count }, (_, index) => (
				<SkeletonBone key={index} variant="goalRow" />
			))}
		</Stack>
	);
};

export const GraphClawComponentErrorState = (): React.JSX.Element => {
	const intl = useIntl();

	return (
		<Flex alignItems="center" gap="space.100" xcss={styles.componentErrorState} role="alert">
			<StatusErrorIcon label="" color={token('color.icon.danger')} />
			<Text color="color.text.danger">
				{intl.formatMessage(messages.altaGraphClawConnectionError)}
			</Text>
		</Flex>
	);
};

export const LivingSummarySkeleton = (): React.JSX.Element => {
	useSkeletonStyles();

	return (
		<Stack space="space.150" role="status" aria-label="Loading living summary">
			<SkeletonBone variant="lineMedium" />
			<Stack space="space.075">
				<SkeletonBone variant="lineFull" />
				<SkeletonBone variant="lineLong" />
				<SkeletonBone variant="lineMedium" />
			</Stack>
			<Stack space="space.075">
				<SkeletonBone variant="lineShort" />
				<SkeletonBone variant="lineFull" />
				<SkeletonBone variant="lineLong" />
			</Stack>
		</Stack>
	);
};

export const LivingSummaryThinkingState = (): React.JSX.Element => (
	<Box
		xcss={styles.livingSummaryThinking}
		role="status"
		aria-label="Thinking deeply on your Teamwork Graph"
	>
		<TeamworkGraphSpinner />
		<Box xcss={styles.twgSpinnerText}>
			<Text color="color.text.subtle" weight="semibold">
				Thinking deeply on your Teamwork Graph
			</Text>
		</Box>
	</Box>
);

const MetricsBarSkeleton = (): React.JSX.Element => (
	<Box xcss={styles.metricsRoot}>
		{Array.from({ length: 4 }, (_, index) => (
			<Box key={index} xcss={index === 0 ? styles.metricFirstCell : styles.metricCell}>
				<Flex alignItems="center" justifyContent="space-between" gap="space.150">
					<Flex alignItems="center" gap="space.050">
						<SkeletonBone variant="avatarSmall" />
						<SkeletonBone variant="metricLabel" />
					</Flex>
					<SkeletonBone variant="metricValue" />
				</Flex>
			</Box>
		))}
	</Box>
);

export const TimelineSkeleton = ({
	showHeading = true,
}: {
	showHeading?: boolean;
}): React.JSX.Element => {
	useSkeletonStyles();

	return (
		<Stack space="space.150" role="status" aria-label="Loading timeline">
			{showHeading && (
				<Flex alignItems="center" justifyContent="space-between">
					<SkeletonBone variant="heading" />
					<SkeletonBone variant="pill" />
				</Flex>
			)}
			<Box xcss={styles.card}>
				<Stack space="space.0">
					{Array.from({ length: 3 }, (_, index) => (
						<Box key={index} xcss={styles.timelineEntry}>
							<Stack space="space.075">
								<SkeletonBone variant="date" />
								<SkeletonBone variant="date" />
							</Stack>
							<Stack space="space.100">
								<SkeletonBone variant="pill" />
								<SkeletonBone variant="lineLong" />
								<SkeletonBone variant="lineFull" />
							</Stack>
							<SkeletonBone variant="avatar" />
						</Box>
					))}
				</Stack>
			</Box>
		</Stack>
	);
};

export const RecentDocumentsSkeleton = ({
	showHeading = true,
}: {
	showHeading?: boolean;
}): React.JSX.Element => {
	useSkeletonStyles();

	return (
		<Stack space="space.150" role="status" aria-label="Loading recent documents">
			{showHeading && <SkeletonBone variant="heading" />}
			<Box xcss={styles.recentGrid}>
				{Array.from({ length: 3 }, (_, index) => (
					<Stack key={index} space="space.150" xcss={styles.card}>
						<Stack space="space.075">
							<SkeletonBone variant="lineLong" />
							<SkeletonBone variant="lineFull" />
							<SkeletonBone variant="lineMedium" />
						</Stack>
						<Flex alignItems="center" justifyContent="space-between">
							<SkeletonBone variant="pill" />
							<SkeletonBone variant="date" />
						</Flex>
					</Stack>
				))}
			</Box>
		</Stack>
	);
};

const RightRailSkeleton = (): React.JSX.Element => (
	<Stack space="space.150">
		<Stack space="space.150" xcss={styles.card}>
			<SkeletonBone variant="heading" />
			<Flex alignItems="center" gap="space.100">
				<SkeletonBone variant="avatar" />
				<SkeletonBone variant="lineMedium" />
			</Flex>
		</Stack>
		<Stack space="space.150" xcss={styles.card}>
			<SkeletonBone variant="heading" />
			<Flex alignItems="center" gap="space.050">
				<SkeletonBone variant="avatar" />
				<SkeletonBone variant="avatar" />
				<SkeletonBone variant="avatar" />
			</Flex>
		</Stack>
		<Stack space="space.150" xcss={styles.paddedCard}>
			<SkeletonBone variant="heading" />
			<GoalsSkeleton count={4} />
		</Stack>
	</Stack>
);

export const InferredProjectsLoadingShell = (): React.JSX.Element => {
	useSkeletonStyles();

	return (
		<Box xcss={styles.pageRoot} role="status" aria-label="Loading inferred projects">
			<Box xcss={styles.layout}>
				<Box xcss={styles.sidebarColumn}>
					<Stack space="space.150" xcss={styles.sidebarPanel}>
						<SkeletonBone variant="sidebarTitle" />
						<Stack space="space.050">
							{Array.from({ length: 3 }, (_, index) => (
								<Flex key={index} alignItems="center" gap="space.100">
									<SkeletonBone variant="iconMedium" />
									<SkeletonBone variant="sidebarText" />
								</Flex>
							))}
						</Stack>
					</Stack>
				</Box>

				<Box xcss={styles.contentShell}>
					<Box xcss={styles.contentLayout}>
						<Stack space="space.300" xcss={styles.mainColumn}>
							<Flex alignItems="center" gap="space.200">
								<SkeletonBone variant="iconLarge" />
								<SkeletonBone variant="title" />
							</Flex>
							<MetricsBarSkeleton />
							<Stack space="space.150" xcss={styles.card}>
								<SkeletonBone variant="heading" />
								<LivingSummarySkeleton />
							</Stack>
							<TimelineSkeleton />
							<RecentDocumentsSkeleton />
						</Stack>

						<Box xcss={styles.rightRail}>
							<RightRailSkeleton />
						</Box>
					</Box>
				</Box>
			</Box>
		</Box>
	);
};
